import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Plus, Check, Share2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useMyList } from "../context/MyListContext";
import { shareVideo, shareExternal } from "../lib/share";
import { useAutoplayPref, useYouTubeEvents } from "../lib/autoplay";
import { TikTokPlayer } from "./TikTokPlayer";

const TIKTOK_HOSTS = [
  ["preconnect", "https://www.tiktok.com"],
  ["dns-prefetch", "https://lf16-tiktok-web.tiktokcdn-us.com"],
  ["dns-prefetch", "https://lf16-tiktok-common.tiktokcdn-us.com"],
  ["dns-prefetch", "https://v16-webapp-prime.tiktok.com"],
];

const useTikTokPreconnect = (enabled) => {
  useEffect(() => {
    if (!enabled) return;
    const links = TIKTOK_HOSTS.map(([rel, href]) => {
      const l = document.createElement("link");
      l.rel = rel;
      l.href = href;
      if (rel === "preconnect") l.crossOrigin = "anonymous";
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l.remove());
  }, [enabled]);
};

const ShortItem = ({ short, active, muted, autoplay, onEnded, onToggleMute, onNeedAuth }) => {
  const { user } = useAuth();
  const { isSaved, toggle } = useMyList();
  const saved = isSaved(short.id);
  const [started, setStarted] = useState(false);
  const isInstagram = short.source === "instagram";
  const isTikTok = short.source === "tiktok";
  const isExternal = isInstagram || isTikTok;
  const ytRef = useRef(null);
  useYouTubeEvents(ytRef, { onEnded }, !isExternal && active && started && autoplay, `${short.id}-${muted}`);

  useEffect(() => {
    if (active) setStarted(true);
  }, [active]);

  const thumb = isExternal
    ? short.thumbnail_url
    : `https://i.ytimg.com/vi/${short.youtube_id}/hqdefault.jpg`;

  const handleSave = (e) => {
    e.stopPropagation();
    if (!user) {
      onNeedAuth();
      return;
    }
    toggle(short.id).then((now) => {
      if (now === true) toast.success("Added to My List");
      else if (now === false) toast("Removed from My List");
    });
  };

  const src = isInstagram
    ? `https://www.instagram.com/reel/${short.shortcode}/embed/captioned/`
    : `https://www.youtube.com/embed/${short.youtube_id}` +
      `?autoplay=1&mute=${muted ? 1 : 0}${autoplay ? "" : `&loop=1&playlist=${short.youtube_id}`}&playsinline=1&rel=0&controls=1&modestbranding=1` +
      `&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <div
      className="relative h-full w-full max-w-[430px] mx-auto flex items-center justify-center"
      data-testid={`short-item-${short.id}`}
    >
      <div className="relative h-full max-h-[86vh] aspect-[9/16] bg-black rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
        {isInstagram && (
          <span
            data-testid={`short-ig-badge-${short.id}`}
            className="absolute top-3 left-3 z-30 inline-flex items-center gap-1.5 bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full shadow-lg"
          >
            From Instagram
          </span>
        )}
        {isTikTok && (
          <span
            data-testid={`short-tt-badge-${short.id}`}
            className="absolute top-3 left-3 z-30 inline-flex items-center gap-1.5 bg-black text-white text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full shadow-lg border border-white/25"
          >
            From TikTok
          </span>
        )}
        {isTikTok && active && <TikTokPlayer short={short} onEnded={autoplay ? onEnded : undefined} />}
        {active && started ? (
          isTikTok ? null : isExternal ? (
            <iframe
              key={short.id}
              data-testid={`short-player-${short.id}`}
              className="w-full h-full bg-white"
              src={src}
              title={short.title}
              scrolling="no"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <iframe
              key={`${short.id}-${muted ? "m" : "u"}`}
              ref={ytRef}
              data-testid={`short-player-${short.id}`}
              className="w-full h-full"
              src={src}
              title={short.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <button
            type="button"
            onClick={() => setStarted(true)}
            className="absolute inset-0 z-10 group"
            aria-label="Play short"
          >
            {thumb ? (
              <img
                src={thumb}
                alt={short.title}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                className="w-full h-full object-cover select-none pointer-events-none"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
            )}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 text-black fill-black ml-1" />
              </div>
            </div>
          </button>
        )}

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-16 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none">
          <p className="text-[11px] text-zinc-300 mb-1">@{short.source_handle}</p>
          <h3 className="text-sm font-semibold text-white line-clamp-2 leading-tight">{short.title}</h3>
        </div>

        {/* Right action rail */}
        <div className="absolute right-2.5 bottom-6 flex flex-col items-center gap-5">
          <button
            data-testid={`short-save-${short.id}`}
            onClick={handleSave}
            className="flex flex-col items-center gap-1 text-white"
          >
            <span
              className={`w-11 h-11 rounded-full backdrop-blur border flex items-center justify-center transition-colors ${
                saved ? "bg-[#FF2A2A]/20 border-[#FF2A2A]" : "bg-black/50 border-white/25 hover:bg-black/70"
              }`}
            >
              {saved ? (
                <Check className="w-5 h-5 text-[#FF2A2A]" strokeWidth={3} />
              ) : (
                <Plus className="w-5 h-5" strokeWidth={2.5} />
              )}
            </span>
            <span className="text-[10px]">{saved ? "Saved" : "Save"}</span>
          </button>

          <button
            data-testid={`short-share-${short.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isExternal) {
                shareExternal(short.url, short.title);
              } else {
                shareVideo(short);
              }
            }}
            className="flex flex-col items-center gap-1 text-white"
          >
            <span className="w-11 h-11 rounded-full bg-black/50 backdrop-blur border border-white/25 flex items-center justify-center hover:bg-black/70 transition-colors">
              <Share2 className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <span className="text-[10px]">Share</span>
          </button>

          {!isExternal && (
            <button
              data-testid="shorts-mute-toggle"
              onClick={(e) => {
                e.stopPropagation();
                onToggleMute();
              }}
              className="flex flex-col items-center gap-1 text-white"
            >
              <span className="w-11 h-11 rounded-full bg-black/50 backdrop-blur border border-white/25 flex items-center justify-center hover:bg-black/70 transition-colors">
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </span>
              <span className="text-[10px]">{muted ? "Muted" : "Sound"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const ShortsFeed = ({ videos, sections = [], initialSection = "All" }) => {
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [filter, setFilter] = useState(sections.includes(initialSection) ? initialSection : "All");
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();
  const [autoplay] = useAutoplayPref();
  useTikTokPreconnect(videos.some((v) => v.source === "tiktok"));

  useEffect(() => {
    setFilter(sections.includes(initialSection) ? initialSection : "All");
  }, [initialSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = filter === "All" ? videos : videos.filter((v) => v.section === filter);

  useEffect(() => {
    setActive(0);
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [filter]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.index));
        });
      },
      { root: containerRef.current, threshold: 0.6 }
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [shown]);

  const chips = sections.length ? ["All", ...sections] : [];

  if (!videos.length) {
    return (
      <div className="mt-16 h-[calc(100vh-4rem)] flex items-center justify-center px-6 text-center">
        <p className="text-zinc-400" data-testid="shorts-empty">
          Fresh Shorts are being pulled in — check back in a moment.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-16 relative">
      {chips.length > 0 && (
        <div
          data-testid="shorts-section-chips"
          className="sticky top-0 z-30 flex gap-2 overflow-x-auto no-scrollbar px-4 py-3 bg-gradient-to-b from-[#030305] via-[#030305]/95 to-transparent"
        >
          {chips.map((c) => (
            <button
              key={c}
              data-testid={`shorts-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.1em] border transition-colors ${
                filter === c
                  ? "bg-white text-black border-white"
                  : "bg-black/50 text-zinc-300 border-white/20 hover:border-white/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="h-[calc(100vh-7rem)] flex items-center justify-center px-6 text-center">
          <p className="text-zinc-400" data-testid="shorts-section-empty">
            No videos in "{filter}" yet.
          </p>
        </div>
      ) : (
        <div
          ref={containerRef}
          data-testid="shorts-feed"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          className={`${chips.length ? "h-[calc(100vh-7rem)]" : "h-[calc(100vh-4rem)]"} overflow-y-scroll snap-y snap-mandatory no-scrollbar bg-[#030305] select-none`}
        >
          {shown.map((s, i) => (
            <div
              key={s.id}
              data-index={i}
              ref={(el) => (itemRefs.current[i] = el)}
              className={`snap-start snap-always ${chips.length ? "h-[calc(100vh-7rem)]" : "h-[calc(100vh-4rem)]"} flex items-center justify-center px-3 py-3`}
            >
              <ShortItem
                short={s}
                active={i === active}
                autoplay={autoplay}
                onEnded={() => itemRefs.current[i + 1]?.scrollIntoView({ behavior: "smooth" })}
                muted={muted}
                onToggleMute={() => setMuted((m) => !m)}
                onNeedAuth={() => navigate("/login")}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
